import React from "react";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "../i18n";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  AdminPage,
  HomePage,
  LoginPage,
  NotFound404Page,
  Forbidden403Page,
  MainMapPage,
  MasterPlanPortalPage,
  MultiplePlotListPage,
} from "../pages";
import { ProtectedRoute } from "./protected-route";
import { getUser } from "../services/actions/user";
import { useAppDispatch } from "../hooks/useAppDispatch";
import Header from "./home/header/header";
import { Roles } from "../types/roles";
import { useAppSelector } from "../hooks/useAppSelector";
import { getLayers } from "../services/actions/map";
import MapFilterPanel from "./features/filters/map-filter-panel/map-filter-panel";
import CitySelectMenu from "./features/cities/city-select-menu/city-select-menu";
import Companies from "./features/companies/companies";
import Products from "./features/products/products";
import FilterWrapper from "./features/filters/filter-wrapper";
import MainFormBlock from "./features/master-plan-modification-portal/operation-setup/main-form";
import GeometryEditor from "./features/master-plan-modification-portal/geometry-editing/geometry-editor/geometry-editor";
import DetailsEditor from "./features/master-plan-modification-portal/details-editing/details-editor/details-editor";
import { RoutePaths } from "../config/routeConfig";
import RepositoryPage from "../pages/repository/repository";
import { fetchFilterData } from "../services/reducers/filters";
import { fetchCitiesData } from "../services/reducers/cities";
import KrookieJournal from "./features/repository/krookie-journal/krookie-journal";
import ReportsJournal from "./features/repository/reports-journal/reports-journal";
import RepositoryIndexRedirect from "../pages/repository/repository-index-redirect";
import useSyncMapSearchParams from "../hooks/useSyncMapSearchParams";
import MasterCreateStudyPage from "../pages/master-create-study-page/master-create-study-page";
import MainFormCreateStudy from "./features/master-create-study/main-form-create-study/main-form-create-study";
import UtilitiesJournal from "./features/repository/utilities-journal/utilities-journal";
import MasterUtilitiesPage from "../pages/master-utilities-page/master-utilities-page";
import { useRestrictAccess } from "../hooks/useRestrictAccess";
import GeometryCreator from "./features/master-plan-modification-portal/geometry-editing/geometry-creator/geometry-creator";
import DetailsCreator from "./features/master-plan-modification-portal/details-editing/details-creator/details-creator";
import GeometryMerger from "./features/master-plan-modification-portal/geometry-editing/geometry-merger/geometry-merger";
import DetailsMerger from "./features/master-plan-modification-portal/details-editing/details-merger/details-merger";
import GeometryDivider from "./features/master-plan-modification-portal/geometry-editing/geometry-divider/geometry-divider";
import DetailsDivider from "./features/master-plan-modification-portal/details-editing/details-divider/details-divider";

export default function App() {
  const dispatch = useAppDispatch();
  const { layers } = useAppSelector((store) => store.map);
  const userIsLoading = useAppSelector((state) => state.user.isLoading);

  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  useEffect(() => {
    if (!userIsLoading && (!layers || layers.length === 0)) {
      dispatch(getLayers());
    }
  }, [dispatch, userIsLoading, layers]);

  useEffect(() => {
    if (layers && layers.length > 0) {
      dispatch(fetchFilterData({ filterType: "Plots" }));
      dispatch(fetchFilterData({ filterType: "InvestmentOpportunities" }));
      dispatch(fetchFilterData({ filterType: "SpacesForRent" }));
      dispatch(fetchCitiesData());
    }
  }, [dispatch, layers]);

  useSyncMapSearchParams([
    {
      path: "/repository",
      exact: false,
    },
    {
      path: "/",
      exact: true,
    },
    {
      path: "/login",
      exact: false,
    },
  ]);

  useRestrictAccess({
    [Roles.Contractor]: ["/repository/utilities", "/utilitiesMaster"],
  });

  return (
    <I18nextProvider i18n={i18n}>
      <Header />
      <Routes>
        <Route path="/">
          <Route
            index
            element={
              <ProtectedRoute roles={[Roles.Unauthorized]}>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route element={<MainMapPage />}>
            <Route index element={<Navigate to="search" replace />} />
            <Route path="search" element={<MapFilterPanel />}>
              <Route path=":filter" element={<FilterWrapper />} />
            </Route>
            <Route path="cities" element={<CitySelectMenu />} />
            <Route path="companies" element={<Companies />} />
            <Route path="products" element={<Products />} />
          </Route>
        </Route>
        <Route
          path={RoutePaths.masterPlan}
          element={
            <ProtectedRoute roles={[Roles.KrookieCreator]}>
              <MasterPlanPortalPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<MainFormBlock />} />
          <Route path="edit">
            <Route index element={<GeometryEditor />} />
            <Route path="details" element={<DetailsEditor />} />
          </Route>

          <Route path="create">
            <Route index element={<GeometryCreator />} />
            <Route path="details" element={<DetailsCreator />} />
          </Route>
          <Route path="merge">
            <Route index element={<GeometryMerger />} />
            <Route path="details" element={<DetailsMerger />} />
          </Route>
          <Route path="divide">
            <Route index element={<GeometryDivider />} />
            <Route path="details" element={<DetailsDivider />} />
          </Route>
        </Route>
        <Route
          path={RoutePaths.multiplePlot}
          element={<MultiplePlotListPage />}
        />
        <Route
          path="/login"
          element={
            <ProtectedRoute roles={[Roles.Unauthorized]}>
              <LoginPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={[Roles.Admin]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound404Page />} />
        <Route path="/404" element={<NotFound404Page />} />
        <Route path="/403" element={<Forbidden403Page />} />
        <Route
          path="/repository"
          element={
            <ProtectedRoute
              roles={[
                Roles.KrookieJournal,
                Roles.ReportsJournal,
                Roles.Contractor,
                Roles.DepartmentSpecialist,
              ]}
            >
              <RepositoryPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<RepositoryIndexRedirect />} />
          <Route
            path="krookie"
            element={
              <ProtectedRoute roles={[Roles.KrookieJournal]}>
                <KrookieJournal />
              </ProtectedRoute>
            }
          />
          <Route
            path="reports"
            element={
              <ProtectedRoute roles={[Roles.ReportsJournal]}>
                <ReportsJournal />
              </ProtectedRoute>
            }
          />
          <Route
            path="utilities"
            element={
              <ProtectedRoute
                roles={[Roles.DepartmentSpecialist, Roles.Contractor]}
              >
                <UtilitiesJournal />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="reportMaster"
          element={
            <ProtectedRoute
              roles={[Roles.StudyCreator]}
              redirectTo="/repository/reports"
            >
              <MasterCreateStudyPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<MainFormCreateStudy />} />
        </Route>
        <Route
          path="utilitiesMaster"
          element={
            <ProtectedRoute
              roles={[Roles.DepartmentSpecialist, Roles.Contractor]}
            >
              <MasterUtilitiesPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </I18nextProvider>
  );
}
